import { c as _c } from "react/compiler-runtime";
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Heading } from '@repo/design-system/components/ui/heading';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import type React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMapDescriptionLinkHandler } from './hooks/useMapDescriptionLinkHandler';
import { transformMapDescriptionUrl } from './map-description-url';
interface LocationContentProps {
  description?: string;
  textAlign?: 'left' | 'center';
  onLinkClick?: (href: string, text: string) => void;
}
export const LocationContent: React.FC<LocationContentProps> = t0 => {
  const $ = _c(19);
  const {
    description,
    textAlign: t1,
    onLinkClick
  } = t0;
  const textAlign = t1 === undefined ? "left" : t1;
  const handleWandLink = useMapDescriptionLinkHandler();
  if (!description) {
    return null;
  }
  let t2;
  if ($[0] !== textAlign) {
    t2 = css.raw({
      textAlign
    });
    $[0] = textAlign;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [remarkGfm];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== handleWandLink || $[4] !== onLinkClick) {
    t4 = t5 => {
      const {
        href: t6,
        children,
        ...props
      } = t5;
      const href = t6 === undefined ? "" : t6;
      const onClick = e => {
        onLinkClick?.(href, e.currentTarget.textContent?.trim() ?? "");
        if (href.startsWith("wemod://") || href.startsWith("wand://")) {
          e.preventDefault();
          handleWandLink(href);
          return;
        }
      };
      return <a {...props} className={css({
        color: "white",
        textDecoration: "underline",
        wordBreak: "break-word",
        _hover: {
          color: "text.secondary",
          cursor: "pointer"
        }
      })} href={href} target={/^https?:\/\//i.test(href) ? "_blank" : undefined} rel={/^https?:\/\//i.test(href) ? "noopener noreferrer" : undefined} onClick={onClick}>{children}</a>;
    };
    $[3] = handleWandLink;
    $[4] = onLinkClick;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] !== textAlign) {
    t5 = t7 => {
      const {
        ...props_9
      } = t7;
      return <th {...props_9} style={{
        padding: "8px 12px",
        background: "rgba(255,255,255,0.06)",
        color: "white",
        fontWeight: 500,
        border: "1px solid rgba(255,255,255,0.08)",
        textAlign
      }} />;
    };
    t6 = t8 => {
      const {
        ...props_10
      } = t8;
      return <td {...props_10} style={{
        padding: "8px 12px",
        color: "white",
        border: "1px solid rgba(255,255,255,0.08)",
        textAlign
      }} />;
    };
    $[6] = textAlign;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      a: t4,
      p: _temp,
      h1: _temp2,
      h2: _temp3,
      h3: _temp4,
      h4: _temp5,
      h5: _temp6,
      h6: _temp7,
      img: _temp8,
      table: _temp9,
      th: t5,
      td: t6,
      ul: _temp10,
      ol: _temp11,
      li: _temp12,
      blockquote: _temp13,
      code: _temp14,
      pre: _temp15,
      hr: _temp16,
      strong: _temp17,
      em: _temp18,
      del: _temp19,
      input: _temp20
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== description || $[14] !== t7) {
    t8 = <ReactMarkdown urlTransform={transformMapDescriptionUrl} remarkPlugins={t3} components={t7}>{description}</ReactMarkdown>;
    $[13] = description;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t8) {
    t9 = <FlexColumn gap="8" width="full" align="stretch" className={t2}>{t8}</FlexColumn>;
    $[16] = t2;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};
function _temp(t0) {
  const {
    ...props_0
  } = t0;
  return <Type {...props_0} variant="body-s" color="white" translate={undefined} className={css({
    marginY: "4"
  })} />;
}
function _temp2(t0) {
  const {
    ...props_1
  } = t0;
  return <Heading {...props_1} level="h1" color="white" className={css.raw({
    fontSize: "lg",
    lineHeight: "[24px]",
    fontWeight: "medium"
  })} translate={undefined} />;
}
function _temp3(t0) {
  const {
    ...props_2
  } = t0;
  return <Heading {...props_2} level="h2" color="white" className={css.raw({
    fontSize: "xl",
    lineHeight: "[28px]",
    fontWeight: "medium"
  })} translate={undefined} />;
}
function _temp4(t0) {
  const {
    ...props_3
  } = t0;
  return <Heading {...props_3} level="h3" color="white" className={css.raw({
    fontSize: "lg",
    lineHeight: "[24px]",
    fontWeight: "medium"
  })} translate={undefined} />;
}
function _temp5(t0) {
  const {
    ...props_4
  } = t0;
  return <Heading {...props_4} level="h4" color="white" className={css.raw({
    fontSize: "lg",
    lineHeight: "[30px]",
    fontWeight: "medium"
  })} translate={undefined} />;
}
function _temp6(t0) {
  const {
    ...props_5
  } = t0;
  return <Heading {...props_5} level="h5" color="white" className={css.raw({
    fontSize: "lg",
    lineHeight: "[36px]",
    fontWeight: "medium"
  })} translate={undefined} />;
}
function _temp7(t0) {
  const {
    ...props_6
  } = t0;
  return <Heading {...props_6} level="h6" color="white" className={css.raw({
    fontSize: "[30px]",
    lineHeight: "[36px]",
    fontWeight: "medium"
  })} translate={undefined} />;
}
function _temp8(t0) {
  const {
    ...props_7
  } = t0;
  return <img {...props_7} loading="lazy" alt="" style={{
    maxWidth: "100%",
    width: "100%",
    borderRadius: 8,
    margin: "8px auto",
    display: "block"
  }} />;
}
function _temp9(t0) {
  const {
    ...props_8
  } = t0;
  return <table {...props_8} style={{
    width: "100%",
    borderCollapse: "collapse",
    background: "rgba(255,255,255,0.05)",
    borderRadius: 8,
    overflow: "hidden",
    color: "white",
    margin: "8px 0",
    fontSize: "14px"
  }} />;
}
function _temp10(t0) {
  const {
    children: children_0,
    ...props_11
  } = t0;
  return <ul {...props_11} className={css({
    listStyleType: "disc",
    marginTop: "0",
    marginLeft: "12",
    marginY: "8",
    paddingLeft: "16",
    color: "text.primary",
    listStylePosition: "outside",
    textAlign: "left"
  })}>{children_0}</ul>;
}
function _temp11(t0) {
  const {
    children: children_1,
    ...props_12
  } = t0;
  return <ol {...props_12} className={css({
    listStyleType: "decimal",
    marginLeft: "12",
    marginY: "8",
    paddingLeft: "16",
    color: "white",
    listStylePosition: "outside",
    textAlign: "left"
  })}>{children_1}</ol>;
}
function _temp12(t0) {
  const {
    children: children_2,
    ...props_13
  } = t0;
  return <li {...props_13} className={css({
    marginY: "8"
  })}><Type as="span" variant="body-s" color="white">{children_2}</Type></li>;
}
function _temp13(t0) {
  const {
    ...props_14
  } = t0;
  return <blockquote {...props_14} className={css({
    marginY: "8",
    paddingX: "12",
    paddingY: "4",
    color: "brand.primary",
    background: "whiteAlpha.5",
    borderRadius: "12"
  })} />;
}
function _temp14(t0) {
  const {
    children: children_3,
    ...props_15
  } = t0;
  return <code {...props_15} className={css({
    background: "whiteAlpha.10",
    paddingX: "4",
    paddingY: "4",
    borderRadius: "2",
    fontFamily: "[monospace]",
    fontSize: "md",
    color: "brand.primary"
  })}>{children_3}</code>;
}
function _temp15(t0) {
  const {
    children: children_4,
    ...props_16
  } = t0;
  return <pre {...props_16} className={css({
    background: "blackAlpha.30",
    padding: "16",
    borderRadius: "4",
    overflow: "auto",
    marginY: "8",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "whiteAlpha.10"
  })}>{children_4}</pre>;
}
function _temp16(t0) {
  const {
    ...props_17
  } = t0;
  return <hr {...props_17} className={css({
    border: "none",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "whiteAlpha.60",
    marginY: "12",
    width: "full"
  })} />;
}
function _temp17(t0) {
  const {
    children: children_5,
    ...props_18
  } = t0;
  return <strong {...props_18} className={css({
    fontWeight: "medium",
    color: "white"
  })}>{children_5}</strong>;
}
function _temp18(t0) {
  const {
    children: children_6,
    ...props_19
  } = t0;
  return <em {...props_19} className={css({
    fontStyle: "italic",
    color: "white"
  })}>{children_6}</em>;
}
function _temp19(t0) {
  const {
    children: children_7,
    ...props_20
  } = t0;
  return <del {...props_20} className={css({
    textDecoration: "line-through",
    color: "whiteAlpha.60"
  })}>{children_7}</del>;
}
function _temp20(t0) {
  const {
    type,
    checked,
    ...props_21
  } = t0;
  if (type === "checkbox") {
    return <input {...props_21} type="checkbox" checked={checked} className={css({
      marginRight: "4"
    })} />;
  }
  return <input {...props_21} type={type} checked={checked} />;
}